/*
 * sr-falls-prompt.js
 *
 * SR FIX - CONVERSION (2026-08-30)
 *
 * THE PROBLEM
 * The "Falls & Edging" dropdown used to be rendered by Myra Infinite Product
 * Options, which showed an action-prompting placeholder. When it was rebuilt in
 * theme code the select was written as:
 *
 *     <select data-falls-select>
 *       <option value="">None</option>
 *       ...
 *
 * With no placeholder, the browser auto-selects the first option, so the field
 * arrives on screen reading "None" - visually identical to a field the customer
 * has already dealt with. Orders for the service fell from ~10/month to ~2-3.
 *
 * WHAT THIS DOES
 * 1. Greys the select while the placeholder is still the chosen option, so it
 *    reads as "waiting for you" rather than "already answered".
 * 2. On the product page, if the customer presses Add to Cart without having
 *    touched the dropdown, one inline prompt appears offering the service. It
 *    is a NUDGE, not a gate: "No thanks" continues immediately, and the second
 *    press always goes through.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 * It does not touch the cart-line logic in native-customization.liquid,
 * customization-sync.js or sr-add-to-bag.js. Those read the dropdown as
 * `if (fallsSelect.value)`, so BOTH the placeholder and "None" must keep
 * `value=""` and both correctly mean "no service". This file therefore never
 * reads or writes `.value` to decide anything - it works off the placeholder
 * option's data attribute and off selectedIndex, leaving value semantics, the
 * variant IDs, `_service_type`, `_customization_id` and the /cart/add.js calls
 * exactly as they were.
 *
 * The nudge intercepts the Add to Cart CLICK, before any submit event fires, so
 * it never has to compete with the theme's own capturing submit handler.
 */
(function () {
  'use strict';

  var PLACEHOLDER = 'option[data-sr-falls-placeholder]';
  var UNSET_CLASS = 'sr-falls-select--unset';
  var ACK = 'data-sr-falls-acknowledged';

  function injectStyles() {
    if (document.getElementById('sr-falls-prompt-styles')) return;
    var s = document.createElement('style');
    s.id = 'sr-falls-prompt-styles';
    s.textContent = [
      // Muted while nothing has been chosen. Same subdued tone the theme uses
      // for hint text, so it reads as a prompt rather than as an error.
      'select.' + UNSET_CLASS + '{color:#8a8177}',
      'select.' + UNSET_CLASS + ' option{color:#2B2B2B}',
      '.sr-falls-nudge{margin-top:10px;padding:12px 14px;border:1px solid #E8E1D6;',
      'border-radius:10px;background:#FBF6EE;font-size:13.5px;line-height:1.5;color:#3c3c3c}',
      '.sr-falls-nudge__title{font-weight:600;color:#2B2B2B;margin:0 0 8px}',
      '.sr-falls-nudge__actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px}',
      '.sr-falls-nudge__btn{font:inherit;font-size:13px;padding:9px 14px;border-radius:999px;',
      'border:1px solid #8B1E3F;background:#8B1E3F;color:#fff;cursor:pointer}',
      '.sr-falls-nudge__btn--ghost{background:transparent;color:#8B1E3F}',
      '.sr-falls-nudge__btn:focus-visible{outline:2px solid #D4AF37;outline-offset:2px}'
    ].join('');
    document.head.appendChild(s);
  }

  function selectedIsPlaceholder(select) {
    var opt = select.options[select.selectedIndex];
    return !!(opt && opt.hasAttribute('data-sr-falls-placeholder'));
  }

  function noneIndex(select) {
    // The explicit "None" choice is the first non-placeholder option that still
    // carries an empty value - i.e. the customer saying "no service", which the
    // cart logic already treats identically to "not chosen".
    for (var i = 0; i < select.options.length; i++) {
      var o = select.options[i];
      if (!o.hasAttribute('data-sr-falls-placeholder') && o.value === '') return i;
    }
    return -1;
  }

  function paint(select) {
    select.classList.toggle(UNSET_CLASS, selectedIsPlaceholder(select));
  }

  function paintAll(scope) {
    var sels = (scope || document).querySelectorAll('select');
    Array.prototype.forEach.call(sels, function (s) {
      if (s.querySelector(PLACEHOLDER)) paint(s);
    });
  }

  // --- greying, everywhere the dropdown appears (PDP, add-to-bag modal, cart drawer)
  injectStyles();
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.tagName === 'SELECT' && t.querySelector(PLACEHOLDER)) paint(t);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { paintAll(); });
  } else {
    paintAll();
  }
  // The modal and the cart drawer render their markup later; repaint when new
  // selects appear rather than polling.
  if ('MutationObserver' in window) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        for (var j = 0; j < muts[i].addedNodes.length; j++) {
          var n = muts[i].addedNodes[j];
          if (n.nodeType === 1) paintAll(n);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // --- soft nudge, product page only -------------------------------------
  // Only the PDP needs it. The add-to-bag modal and the cart-drawer upsell ARE
  // themselves the prompt - they open after the customer has already pressed
  // add to cart, so a second prompt inside them would just be a double ask.

  function buildNudge(select, addButton) {
    var box = document.createElement('div');
    box.className = 'sr-falls-nudge';
    box.setAttribute('role', 'status');

    var title = document.createElement('p');
    title.className = 'sr-falls-nudge__title';
    title.textContent = 'Would you like falls & edging stitched?';
    box.appendChild(title);

    var body = document.createElement('p');
    body.style.margin = '0';
    body.textContent = 'Your saree arrives ready to wear. Machine stitched +₹200, hand stitched +₹350.';
    box.appendChild(body);

    var actions = document.createElement('div');
    actions.className = 'sr-falls-nudge__actions';

    var choose = document.createElement('button');
    choose.type = 'button';
    choose.className = 'sr-falls-nudge__btn';
    choose.textContent = 'Choose an option';
    choose.addEventListener('click', function () {
      box.remove();
      select.focus();
      try { select.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { select.scrollIntoView(); }
    });

    var skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'sr-falls-nudge__btn sr-falls-nudge__btn--ghost';
    skip.textContent = 'No thanks, continue';
    skip.addEventListener('click', function () {
      var idx = noneIndex(select);
      if (idx > -1) {
        select.selectedIndex = idx;
        // Both options carry value="", so this changes nothing the cart logic
        // can observe - it only records that the customer has decided.
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      box.remove();
      addButton.setAttribute(ACK, '1');
      addButton.click();
    });

    actions.appendChild(choose);
    actions.appendChild(skip);
    box.appendChild(actions);
    return box;
  }

  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest
      ? event.target.closest('buy-buttons button[type="submit"]')
      : null;
    if (!btn) return;
    if (btn.hasAttribute(ACK)) { btn.removeAttribute(ACK); return; } // second press - let it through

    var form = btn.form || btn.closest('form');
    var scope = document.querySelector('[data-native-customization]');
    if (!scope) return;

    var select = scope.querySelector('select[data-falls-select]');
    if (!select || !select.querySelector(PLACEHOLDER)) return;
    if (!selectedIsPlaceholder(select)) return;      // already decided - never interrupt
    if (scope.querySelector('.sr-falls-nudge')) return; // prompt already showing

    event.preventDefault();
    event.stopPropagation();

    var nudge = buildNudge(select, btn);
    (select.closest('.v-stack') || select.parentNode).appendChild(nudge);
    try { nudge.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) {}
  }, true);
})();
